import { observer } from 'mobx-react'
import React, { FC, useEffect, useRef, useState } from 'react'

import { CheckPicker, CheckPickerItemTypeNew } from '@avantstay/backoffice-core'

import { RegionsStore } from '@stores/RegionsStore'

export interface RegionsFilterProps {
  dataCy?: string
  initialValue?: string[]
  onChange: (value?: string[]) => void
  noRegionOption?: boolean
  placeholder?: string
  className?: string
  hideOptionAll?: boolean
  hasClearOption?: boolean
  customOptions?: CheckPickerItemTypeNew<string>[]
  isLoadingOptionsOverride?: boolean
  dropdownMaxHeight?: number
  shouldApplyOnClose?: boolean
}

const _RegionsFilterNew: FC<RegionsFilterProps> = ({
  className,
  dataCy,
  initialValue,
  noRegionOption,
  onChange,
  placeholder,
  hideOptionAll = false,
  hasClearOption = false,
  customOptions,
  isLoadingOptionsOverride,
  dropdownMaxHeight,
  shouldApplyOnClose = false,
}) => {
  const { regionsOptionsNew, isLoadingRegionOptions } = RegionsStore

  const baseOptions =
    customOptions ||
    regionsOptionsNew?.map(option => ({
      value: option.value,
      label: option.label,
    }))

  const allOptions = noRegionOption
    ? [
        { value: '00000000-0000-0000-0000-000000000000', label: 'No region' },
        ...(baseOptions || []),
      ]
    : baseOptions

  const [selectedRegions, setSelectedRegions] = useState<string[] | undefined>(initialValue)

  // Read on close: the clear icon also toggles the menu, so both fire before a re-render
  const latestRegionsRef = useRef(initialValue)

  // This clears selection when we run PropertiesListStore.resetFilters
  useEffect(() => {
    if (initialValue === undefined) {
      setSelectedRegions(undefined)
    }

    if (!!initialValue && initialValue !== selectedRegions) {
      setSelectedRegions(initialValue)
    }

    latestRegionsRef.current = initialValue
  }, [initialValue])

  const regionsLength = baseOptions?.length

  const inputPlaceholder =
    placeholder ?? `All Markets ${regionsLength > 0 ? `(${regionsLength})` : ''}`

  const handleSelect = (value: string[]) => {
    setSelectedRegions(value)
    latestRegionsRef.current = value

    if (!shouldApplyOnClose) {
      onChange(value)
    }
  }

  const handleCloseMenu = () => {
    const selected = latestRegionsRef.current ?? []
    const applied = initialValue ?? []
    const hasChanged =
      selected.length !== applied.length || selected.some(id => !applied.includes(id))

    if (hasChanged) {
      onChange(selected)
    }
  }

  return (
    <CheckPicker.SmallNew
      className={className}
      data-cy={dataCy}
      searchable
      hideOptionAll={hideOptionAll}
      options={allOptions || []}
      placeholder={inputPlaceholder}
      value={selectedRegions || []}
      onChange={handleSelect}
      isLoadingOptions={
        isLoadingOptionsOverride !== undefined ? isLoadingOptionsOverride : isLoadingRegionOptions
      }
      dropdownWidth={'300px'}
      dropdownMaxHeight={dropdownMaxHeight}
      onCloseMenu={shouldApplyOnClose ? handleCloseMenu : undefined}
      onClearSelection={
        hasClearOption && selectedRegions?.length ? () => handleSelect([]) : undefined
      }
    />
  )
}

export const RegionsFilterNew = observer(_RegionsFilterNew)
